<template>
  <div
    class="mobile-customer-select"
    :class="{ 'mobile-inline-customer-select': inline }"
    :style="wrapperStyle"
  >
    <div v-if="disabled" class="disabled-box"></div>
    <div class="customer-select__drawer" @click="handleClick">
      <div v-if="label" class="customer-select__name text-overflow">{{ label }}</div>
      <div v-else class="customer-select__placeholder">{{ placeholder }}</div>
      <i class="icon-arrow-down-s-line t-iconfont" />
    </div>
  </div>
  <van-popup
    v-model:show="showPicker"
    class="mobile-select-trigger-popup"
    destroy-on-close
    position="bottom"
    round
  >
    <div class="picker-panel safe-area-inset-bottom">
      <div class="head">
        <div class="left" @click="showPicker = false">
          <i class="icon-close-line t-iconfont" />
        </div>
        <div class="center">{{ title }}</div>
        <div class="right">
          <slot name="header-right" />
        </div>
      </div>
      <template v-if="normalizedColumns.length === 0">
        <slot name="empty">
          <div class="no-customer">
            <span>暂无数据</span>
          </div>
        </slot>
      </template>
      <template v-else>
        <div class="customer-select-content">
          <template v-for="(item, index) in normalizedColumns" :key="`${item.value}-${index}`">
            <div
              class="customer-select-item"
              :class="{ 'is-active': isSelected(item) }"
              @click="handleSelect(item, index)"
            >
              <span class="text-overflow">{{ item.text }}</span>
              <i
                v-if="isSelected(item)"
                class="customer-select-item__icon icon-check-line t-iconfont"
              />
            </div>
            <div v-if="index !== normalizedColumns.length - 1" class="bottom-line"></div>
          </template>
        </div>
      </template>
    </div>
  </van-popup>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  label: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: '请选择'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  inline: {
    type: Boolean,
    default: false
  },
  wrapperStyle: {
    type: [String, Object, Array],
    default: ''
  },
  title: {
    type: String,
    default: '请选择'
  },
  columns: {
    type: Array,
    default: () => []
  },
  pickerValue: {
    type: Array,
    default: () => []
  },
  beforeOpen: {
    type: Function,
    default: null
  }
})

const emit = defineEmits(['confirm'])
const showPicker = ref(false)

const normalizedColumns = computed(() =>
  props.columns.map((item) => {
    if (typeof item === 'object' && item !== null) {
      return {
        text: item.text ?? item.label ?? item.name ?? '',
        value: item.value ?? item.id ?? item.text ?? item.label,
        raw: item
      }
    }

    return {
      text: item,
      value: item,
      raw: item
    }
  })
)

const handleClick = async () => {
  if (props.disabled) return
  if (props.beforeOpen) {
    await props.beforeOpen()
  }
  showPicker.value = true
}

const isSelected = (item) => `${props.pickerValue?.[0] ?? ''}` === `${item.value ?? ''}`

const handleSelect = (item, index) => {
  showPicker.value = false
  emit('confirm', {
    selectedValues: [item.value],
    selectedOptions: [item.raw],
    selectedIndexes: [index]
  })
}
</script>

<style scoped lang="less">
.mobile-customer-select {
  flex: 1;
  position: relative;
  width: 100%;
  overflow: hidden;

  &.mobile-inline-customer-select {
    width: 104px;
    margin-right: 8px;
    flex: 0 0 auto;
  }

  .disabled-box {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(243, 246, 249, 0.6);
    z-index: 10;
    cursor: not-allowed;
  }

  .customer-select__drawer {
    cursor: pointer;
    flex: 1;
    border-radius: 4px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 6px 12px;
    background: #ffffff;
    box-sizing: border-box;
    border: 1px solid #e5e6eb;
    width: 100%;
  }

  .customer-select__name {
    margin-right: 8px;
    width: 100%;
    line-height: 18px;
    flex: 1;
  }

  .customer-select__placeholder {
    color: #c8c9cc;
    line-height: 18px;
    flex: 1;
  }

  i {
    line-height: 18px;
    color: #969799;
  }
}

.mobile-select-trigger-popup {
  :deep(.van-popup) {
    border-radius: 20px 20px 0 0;
  }

  .picker-panel {
    display: flex;
    flex-direction: column;
    max-height: 80vh;

    .head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 13px 16px;
      color: #323233;
    }

    .left,
    .right {
      flex: 0 0 52px;
      display: flex;
      align-items: center;
      min-height: 24px;
    }

    .left {
      justify-content: flex-start;

      i {
        font-size: 24px;
        line-height: 24px;
        color: #969799;
      }
    }

    .center {
      flex: 1;
      text-align: center;
      font-size: 16px;
      font-weight: 600;
      color: #323233;
      line-height: 22px;
    }

    .right {
      justify-content: flex-end;
    }

    .no-customer {
      margin-top: 24px;
      display: flex;
      justify-content: center;
      font-family: PingFang SC;
      font-size: 14px;
      line-height: 20px;
      color: #969799;
      padding-bottom: 24px;
    }

    .customer-select-content {
      flex: 1;
      // 防止在手机端被遮挡
      padding-bottom: 10px;
      overflow-y: auto;

      .customer-select-item {
        height: 48px;
        padding: 0 28px;
        line-height: 48px;
        box-sizing: border-box;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: space-between;
        color: #323233;

        &.is-active {
          color: #7a52f4;
        }

        .text-overflow {
          flex: 1;
        }

        .customer-select-item__icon {
          flex: 0 0 auto;
          margin-left: 8px;
          font-size: 20px;
          line-height: 20px;
          color: #7a52f4;
        }
      }

      .bottom-line {
        height: 1px;
        background: #e5e6eb;
        transform: scaleY(0.5);
      }
    }
  }
}

.text-overflow {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
